import { useMutation } from '@tanstack/react-query';
import { queryClient, useStanQuery } from '@/queries';
import { QUERY_KEYS } from '@/lib';
import {
    assignExperiment,
    getExperimentCommunityStats,
    getExperimentMetadata,
    getExperimentsCommunities,
} from '@/lib/api/experiments';
import { ExperimentData } from '@/types';
import { useAuthStore } from '@/stores';

export function useExperimentQueries() {
    const token = useAuthStore(state => state.token);
    const enabled = useAuthStore(state => state.isAuthenticated);

    /*
     * Assigns user to experiments and forces a re-fetch of the user data
     */
    const { mutate: assignUserToExperimentsMutation } = useMutation({
        mutationFn: async (assignments: ExperimentData) => {
            const allAssignments = [];
            for (const experiment of Object.keys(assignments)) {
                allAssignments.push(assignExperiment(experiment, assignments[experiment] as 0 | 1));
            }
            await Promise.all(allAssignments);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPERIMENTS] });
        },
    });

    /*
     * Joins / Retrieves experiment community assignments
     */
    const { data: experimentsCommunities, isLoading: isExperimentCommunitiesLoading } = useStanQuery(
        {
            queryKey: [QUERY_KEYS.EXPERIMENTS, 'communities', token],
            queryFn: getExperimentsCommunities,
            staleTime: 1000 * 60 * 5, // 5 minutes
            enabled,
            retry: false,
            meta: { error: { sentryMessage: '[EXPERIMENTS] failed-to-fetch-experiments-communities' } },
        },
        true // Skip page loading - experiments are global, pages should handle their own loading states
    );

    /*
     * Joins / Retrieves experiment community assignments
     */
    const { data: experimentsCommunityStats, isLoading: isExperimentCommunityStatsLoading } = useStanQuery(
        {
            queryKey: [QUERY_KEYS.EXPERIMENTS, 'community-stats', token],
            queryFn: getExperimentCommunityStats,
            staleTime: 1000 * 60 * 5, // 5 minutes
            enabled,
            retry: false,
            meta: { error: { sentryMessage: '[EXPERIMENTS] failed-to-fetch-experiments-community-stats' } },
        },
        true // Skip page loading - experiments are global, pages should handle their own loading states
    );

    /*
     * Joins / Retrieves experiment community assignments
     */
    const { data: experimentsMetadata, isLoading: isExperimentMetadataLoading } = useStanQuery(
        {
            queryKey: [QUERY_KEYS.EXPERIMENTS, 'metadata', token],
            queryFn: getExperimentMetadata,
            staleTime: 1000 * 60 * 5, // 5 minutes
            enabled,
            retry: false,
            meta: { error: { sentryMessage: '[EXPERIMENTS] failed-to-fetch-experiments-metadata' } },
        },
        true // Skip page loading - experiments are global, pages should handle their own loading states
    );

    return {
        assignUserToExperimentsMutation,
        experimentsCommunities,
        isExperimentCommunitiesLoading,
        experimentsCommunityStats,
        isExperimentCommunityStatsLoading,
        experimentsMetadata,
        isExperimentMetadataLoading,
    };
}
